import React, { useEffect, useRef, useState, useCallback } from "react";
import "../../../styles/kanbans/new-board-modal.css";

export interface NewBoardModalProps {
  open: boolean;
  onClose: () => void;
  onCreate?: (name: string) => void;
  initialName?: string;
}

export default function NewBoardModal({
  open,
  onClose,
  onCreate,
  initialName = "",
}: NewBoardModalProps) {
  const [name, setName] = useState(initialName);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(initialName);
  }, [open, initialName]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const onEsc = useCallback(
    (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === "Escape") onClose();
    },
    [open, onClose]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onEsc]);

  if (!open) return null;

  const canCreate = name.trim().length > 0;

  const handleOverlayMouseDown = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleCreate = () => {
    if (!canCreate) return;
    onCreate?.(name.trim());
    onClose();
  };

  return (
    <div
      className="nbm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="nbm-title"
      ref={overlayRef}
      onMouseDown={handleOverlayMouseDown}
    >
      <div className="nbm-modal" tabIndex={-1}>
        <div className="nbm-header">
          <h2 id="nbm-title" className="nbm-title">New Board</h2>
          <button
            type="button"
            className="nbm-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="nbm-divider" />

        <div className="nbm-body">
          <div className="nbm-callout">
            <div className="nbm-callout-title">
              Are you sure you want to create a new board?
            </div>
            <div className="nbm-callout-text">
              We suggest having only one board per job search throughout your
              career; since metrics, activities and other features are
              constrained within each board.
            </div>
          </div>

          <input
            ref={inputRef}
            type="text"
            placeholder="Board Name (e.g., Job Search Winter 2023)"
            className="nbm-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreate();
            }}
            aria-label="Board name"
          />

          <button
            className={`nbm-primary ${!canCreate ? "nbm-disabled" : ""}`}
            onClick={handleCreate}
            disabled={!canCreate}
            type="button"
          >
            Create Board
          </button>
        </div>
      </div>
    </div>
  );
}